/* Predictive search — Shopify's own native /search/suggest.json endpoint
   (see layout/theme.liquid for why: this store has no search app actually
   active today), so this works with zero app dependency and never builds
   a fake local-only index. Debounced, cancels stale requests, minimal
   listbox keyboard support (Up/Down/Enter/Escape). */
(function () {
  'use strict';

  var DEBOUNCE_MS = 200;
  var MIN_CHARS = 2;
  var PRODUCT_LIMIT = 6;
  var COLLECTION_LIMIT = 3;
  var ARTICLE_LIMIT = 2;

  var debounceTimer = null;
  var activeController = null;
  var activeIndex = -1;

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function renderResults(container, input, data) {
    var products = (data.resources.results.products || []).slice(0, PRODUCT_LIMIT);
    var collections = (data.resources.results.collections || []).slice(0, COLLECTION_LIMIT);
    var articles = (data.resources.results.articles || []).slice(0, ARTICLE_LIMIT);
    var pages = (data.resources.results.pages || []).slice(0, ARTICLE_LIMIT);

    if (!products.length && !collections.length && !articles.length && !pages.length) {
      container.innerHTML = '<p class="mbg-predictive-search__empty">No results found.</p>';
      container.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      return;
    }

    var html = '';

    // Collections first — a matched collection (e.g. "hood" -> Hoodies)
    // is the closest thing to a category suggestion Shopify's native
    // predictive search returns, so it leads rather than following
    // individual products, per the requested hierarchy.
    if (collections.length) {
      html += '<div class="mbg-predictive-search__group">';
      html += '<p class="mbg-predictive-search__group-label">Categories</p>';
      collections.forEach(function (collection) {
        html +=
          '<a href="' + escapeHtml(collection.url) + '" class="mbg-predictive-search__item mbg-predictive-search__item--collection" role="option">' +
          '<span class="mbg-predictive-search__item-title">' + escapeHtml(collection.title) + '</span>' +
          '</a>';
      });
      html += '</div>';
    }

    if (products.length) {
      html += '<div class="mbg-predictive-search__group">';
      html += '<div class="mbg-predictive-search__group-head"><p class="mbg-predictive-search__group-label">Products</p><span class="mbg-predictive-search__hint">Press enter to open</span></div>';
      products.forEach(function (product) {
        var image = product.featured_image ? product.featured_image.url : product.image;
        html +=
          '<a href="' + escapeHtml(product.url) + '" class="mbg-predictive-search__item" role="option">' +
          (image ? '<img src="' + escapeHtml(image) + '&width=80" alt="" loading="lazy" width="40" height="50">' : '<span class="mbg-predictive-search__item-noimage"></span>') +
          '<span class="mbg-predictive-search__item-info">' +
          '<span class="mbg-predictive-search__item-title">' + escapeHtml(product.title) + '</span>' +
          '<span class="mbg-predictive-search__item-price">' + product.price + '</span>' +
          '</span>' +
          '</a>';
      });
      html += '</div>';
    }

    var articlesAndPages = articles.concat(pages);
    if (articlesAndPages.length) {
      html += '<div class="mbg-predictive-search__group">';
      html += '<p class="mbg-predictive-search__group-label">Pages &amp; articles</p>';
      articlesAndPages.forEach(function (item) {
        html +=
          '<a href="' + escapeHtml(item.url) + '" class="mbg-predictive-search__item mbg-predictive-search__item--collection" role="option">' +
          '<span class="mbg-predictive-search__item-title">' + escapeHtml(item.title) + '</span>' +
          '</a>';
      });
      html += '</div>';
    }

    var query = input.value.trim();
    html +=
      '<a href="/search?q=' + encodeURIComponent(query) + '" class="mbg-predictive-search__view-all">' +
      'View all results for "' + escapeHtml(query) + '"' +
      '</a>';

    container.innerHTML = html;
    container.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    activeIndex = -1;
  }

  function clearResults(container, input) {
    container.innerHTML = '';
    container.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    activeIndex = -1;
  }

  function search(input, container) {
    var query = input.value.trim();
    if (query.length < MIN_CHARS) {
      clearResults(container, input);
      return;
    }

    if (activeController) {
      activeController.abort();
    }
    activeController = new AbortController();

    var url =
      '/search/suggest.json?q=' + encodeURIComponent(query) +
      '&resources[type]=product,collection,article,page' +
      '&resources[limit]=' + PRODUCT_LIMIT +
      '&resources[options][unavailable_products]=last';

    fetch(url, { signal: activeController.signal })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Predictive search request failed');
        }
        return response.json();
      })
      .then(function (data) {
        renderResults(container, input, data);
      })
      .catch(function (error) {
        if (error.name !== 'AbortError') {
          clearResults(container, input);
        }
      });
  }

  document.addEventListener('input', function (evt) {
    var input = evt.target.closest('[data-mbg-predictive-search-input]');
    if (!input) {
      return;
    }
    var form = input.closest('[data-mbg-predictive-search-form]');
    var container = form && form.parentElement.querySelector('[data-mbg-predictive-results]');
    if (!container) {
      return;
    }
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(function () {
      search(input, container);
    }, DEBOUNCE_MS);
  });

  document.addEventListener('keydown', function (evt) {
    var input = evt.target.closest('[data-mbg-predictive-search-input]');
    if (!input) {
      return;
    }
    var form = input.closest('[data-mbg-predictive-search-form]');
    var container = form && form.parentElement.querySelector('[data-mbg-predictive-results]');
    if (!container || container.hidden) {
      return;
    }
    var items = container.querySelectorAll('[role="option"]');
    if (!items.length) {
      return;
    }

    if (evt.key === 'ArrowDown') {
      evt.preventDefault();
      activeIndex = Math.min(activeIndex + 1, items.length - 1);
    } else if (evt.key === 'ArrowUp') {
      evt.preventDefault();
      activeIndex = Math.max(activeIndex - 1, 0);
    } else if (evt.key === 'Enter' && activeIndex > -1) {
      evt.preventDefault();
      items[activeIndex].click();
      return;
    } else if (evt.key === 'Escape') {
      clearResults(container, input);
      return;
    } else {
      return;
    }

    items.forEach(function (item, index) {
      item.classList.toggle('is-active', index === activeIndex);
    });
    items[activeIndex].scrollIntoView({ block: 'nearest' });
  });

  document.addEventListener('click', function (evt) {
    if (evt.target.closest('[data-mbg-predictive-search-input], [data-mbg-predictive-results]')) {
      return;
    }
    document.querySelectorAll('[data-mbg-predictive-results]').forEach(function (container) {
      var input = container.parentElement.querySelector('[data-mbg-predictive-search-input]');
      if (input) {
        clearResults(container, input);
      }
    });
  });
})();
